import { useState } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { OrganizationService } from "@common/types/organizationService";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { Section } from "@design-system/Section/Section";
import { OrganizationServiceFormModal } from "../OrganizationServices/OrganizationServiceFormModal";
import { OrganizationServicesList } from "../OrganizationServices/OrganizationServicesList";
import { OrganizationCloseStageFormModal } from "../OrganizationCloseStages/OrganizationCloseStageFormModal";
import { OrganizationCloseStagesList } from "../OrganizationCloseStages/OrganizationCloseStagesList";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";

export const OrganizationClosesSettingsPage = () => {
    const canManage = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_SETTINGS,
    );

    const [tiersSearch, setTiersSearch] = useState("");
    const [isTierCreateModalOpen, setIsTierCreateModalOpen] = useState(false);
    const [editingService, setEditingService] =
        useState<OrganizationService | null>(null);
    const [deletingService, setDeletingService] =
        useState<OrganizationService | null>(null);

    const [stagesSearch, setStagesSearch] = useState("");
    const [isStageCreateModalOpen, setIsStageCreateModalOpen] = useState(false);

    // Any open page-level modal must pause the close-stages DnD: its
    // body-wide MutationObserver otherwise interferes with sibling modal mounts
    // (causes inline rendering of newly opened modals like "Create tier").
    const isAnyActionActive =
        isTierCreateModalOpen ||
        isStageCreateModalOpen ||
        !!editingService ||
        !!deletingService;

    return (
        <div>
            <HStack as="header" align="start" className="settings__header">
                <h3 className="settings__heading">Service templates</h3>
            </HStack>

            <div>
                <Section heading="Tiers">
                    <TablePageHeader
                        search={tiersSearch}
                        onSearchChange={setTiersSearch}
                    >
                        {canManage && (
                            <WithHotkey
                                hotKey="alt+n"
                                hotKeyOptions={{
                                    description: "Create tier",
                                    scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                                    preventDefault: true,
                                }}
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() =>
                                        setIsTierCreateModalOpen(true)
                                    }
                                >
                                    New tier
                                </Button>
                            </WithHotkey>
                        )}
                    </TablePageHeader>

                    <OrganizationServicesList
                        search={tiersSearch}
                        canManage={canManage}
                        editingService={editingService}
                        onEditingServiceChange={setEditingService}
                        deletingService={deletingService}
                        onDeletingServiceChange={setDeletingService}
                    />

                    <OrganizationServiceFormModal
                        show={isTierCreateModalOpen}
                        onHide={() => setIsTierCreateModalOpen(false)}
                    />
                </Section>

                <Section heading="Stages">
                    <TablePageHeader
                        search={stagesSearch}
                        onSearchChange={setStagesSearch}
                    >
                        {canManage && (
                            <WithHotkey
                                hotKey="alt+m"
                                hotKeyOptions={{
                                    description: "Create stage",
                                    scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                                    preventDefault: true,
                                }}
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() =>
                                        setIsStageCreateModalOpen(true)
                                    }
                                >
                                    New stage
                                </Button>
                            </WithHotkey>
                        )}
                    </TablePageHeader>

                    <OrganizationCloseStagesList
                        search={stagesSearch}
                        canManage={canManage}
                        isExternalActionActive={isAnyActionActive}
                    />

                    <OrganizationCloseStageFormModal
                        show={isStageCreateModalOpen}
                        onHide={() => setIsStageCreateModalOpen(false)}
                    />
                </Section>
            </div>
        </div>
    );
};
